import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings,
  LayoutDashboard,
  CheckSquare,
  HardDrive,
  Briefcase,
  LogOut,
  Search,
  type LucideIcon,
} from "lucide-react";
import { useAuthStore } from "@groot/shell/store/auth";
import { useCommandPaletteStore } from "@groot/shell/store/command-palette";
import { Dialog, DialogContent, DialogTitle } from "@groot/ui/dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@groot/ui/command";

/** A single selectable entry inside a command-palette group. */
export interface CommandItemEntry {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  variant?: "default" | "destructive";
  /** Stable React key. Defaults to `label`; set when labels may duplicate. */
  id?: string;
}

/** A labeled group of commands in the palette. */
export interface CommandGroupEntry {
  heading: string;
  items: CommandItemEntry[];
  /** Stable React key. Defaults to `heading`; set when headings may duplicate. */
  id?: string;
}

/** Button that opens the shared command palette dialog. */
export function CommandPaletteTrigger({ iconOnly = false }: { iconOnly?: boolean }) {
  const setOpen = useCommandPaletteStore((state) => state.setOpen);
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label="Search (⌘K)"
      className={
        iconOnly
          ? "inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          : "inline-flex items-center justify-between whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input hover:bg-accent hover:text-accent-foreground px-4 py-2 relative h-8 w-full justify-start rounded-[0.5rem] bg-muted/50 text-sm font-normal text-muted-foreground shadow-none sm:pr-12 md:w-40 lg:w-64"
      }
    >
      {iconOnly ? (
        <Search className="size-4" />
      ) : (
        <>
          <span className="hidden lg:inline-flex">Search...</span>
          <span className="inline-flex lg:hidden">Search...</span>
          <kbd className="pointer-events-none absolute right-[0.3rem] top-[0.3rem] hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
            <span className="text-xs">Cmd</span>K
          </kbd>
        </>
      )}
    </button>
  );
}

/** The actual command palette dialog — render this once at the app root. */
export function CommandPaletteDialog({ groups }: { groups?: CommandGroupEntry[] }) {
  const open = useCommandPaletteStore((state) => state.open);
  const setOpen = useCommandPaletteStore((state) => state.setOpen);
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);

  const runCommand = React.useCallback(
    (command: () => unknown) => {
      setOpen(false);
      command();
    },
    [setOpen],
  );

  const handleLogout = () => {
    runCommand(() => {
      logout();
      navigate("/login");
    });
  };

  const resolvedGroups: CommandGroupEntry[] = groups ?? [
    {
      heading: "Navigation",
      items: [
        {
          label: "Dashboard",
          icon: LayoutDashboard,
          onSelect: () => runCommand(() => navigate("/")),
        },
        { label: "Todos", icon: CheckSquare, onSelect: () => runCommand(() => navigate("/todos")) },
        {
          label: "Storage",
          icon: HardDrive,
          onSelect: () => runCommand(() => navigate("/storage")),
        },
        { label: "Jobs", icon: Briefcase, onSelect: () => runCommand(() => navigate("/jobs")) },
        {
          label: "Settings",
          icon: Settings,
          onSelect: () => runCommand(() => navigate("/settings")),
        },
      ],
    },
    {
      heading: "Account",
      items: [
        {
          label: "Logout",
          icon: LogOut,
          onSelect: handleLogout,
          variant: "destructive",
        },
      ],
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* A palette is not a form: nothing to discard, so a click outside
          should dismiss it (the default DialogContent prevents outside
          clicks to protect form input). */}
      <DialogContent
        dismissOnOutsideClick
        className="overflow-hidden p-0 shadow-2xl [&>button]:hidden"
      >
        <DialogTitle className="sr-only">Command Menu</DialogTitle>
        <Command className="[&_[cmdk-input-wrapper]]:border-b-0">
          <CommandInput placeholder="Type a command or search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {resolvedGroups.map((group, idx) => (
              <React.Fragment key={group.id ?? group.heading}>
                {idx > 0 && <CommandSeparator />}
                <CommandGroup heading={group.heading}>
                  {group.items.map((entry) => {
                    const Icon = entry.icon;
                    return (
                      <CommandItem
                        key={entry.id ?? entry.label}
                        onSelect={() => runCommand(entry.onSelect)}
                        className={
                          entry.variant === "destructive"
                            ? "text-destructive data-[selected=true]:text-destructive"
                            : undefined
                        }
                      >
                        {Icon ? <Icon /> : null}
                        <span>{entry.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </React.Fragment>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/** @deprecated Use `CommandPaletteTrigger` for the button and `CommandPaletteDialog` for the dialog. */
export function CommandPalette({ iconOnly = false }: { iconOnly?: boolean }) {
  return (
    <>
      <CommandPaletteTrigger iconOnly={iconOnly} />
      <CommandPaletteDialog />
    </>
  );
}
